/**
 * @GestonalePD Admin club users page
 */
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Paper,
  TableRow,
  TableContainer,
  TableBody,
  Table,
  TableCell,
  TableHead,
  Box,
  InputAdornment,
  TablePagination,
  TableFooter,
  Container,
  Card,
  useMediaQuery,
  Typography,
} from '@mui/material';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import Lock from '@mui/icons-material/Https';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PublicIcon from '@mui/icons-material/Public';
import TurnedInIcon from '@mui/icons-material/TurnedIn';
import TurnedInNotIcon from '@mui/icons-material/TurnedInNot';
import { Search as SearchIcon, KeyboardArrowRight } from '@mui/icons-material';
//-----------------------------------------------------------------------------
import { CommonLabel } from '../../../../utils/Common';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { TablePaginationActions } from '../../../../utils/TableStyles';
import { CustomTextField } from '../../../../utils/StyledInputs';
import { CustomIconButton } from '../../../../utils/Buttons';
import API from '../../../../api';

export default function ClubUser() {
  // general
  const navigate = useNavigate();
  const { state } = useLocation();
  const { clubID } = state;
  const [userList, setUserList] = useState([]);
  const [entityData, setEntityData] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const is400Screen = useMediaQuery('(min-width: 400px)');
  const [currentProfile, setCurrentProfile] = useState('');

  // table pagination handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - userList.length) : 0;
  const handleChangePage = (event, newPage) => setPage(newPage);

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // searchbar handler
  const [searchMaxLimit, setSearchMaxLimit] = useState(100);
  const [onSearch, setSearch] = useState(false);

  // federation club user search requests
  const onSearchUser = (searchedVal) => {
    const name = searchedVal.target.value;
    setSearch(true);
    setErrorMessage('');

    if (name.length <= searchMaxLimit) {
      API.get(`/entities/${clubID}?expand=users&search=${name}`)
        .then((response) => {
          setUserList(response.data.users);

          if (response.data.users.length === 0) {
            setSearchMaxLimit(name.length);
          } else {
            setSearchMaxLimit(name.length + 1);
          }

          if (name.length === 0) {
            setSearch(false);
          }
        })
        .catch((error) => {
          setSearch(false);
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  // get federation club users
  const getClubUsers = () => {
    setErrorMessage('');

    API.get(`/entities/${clubID}?expand=users`)
      .then((response) => {
        setEntityData(response.data);

        setUserList(response.data.users);
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => setLoading(false));
  };

  // Initial federation club user data loading
  useEffect(() => {
    getClubUsers();
  }, []);

  useEffect(() => {
    console.log(errorMessage);
  }, [errorMessage]);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  // if club id not found redirect to 404 page
  if (!clubID) return <Navigate to={{ pathname: '/404' }} />;

  return !isLoading ? (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" role="presentation" sx={{ marginBottom: '12px' }}>
        <Typography
          sx={{
            height: '24px',
            width: 'fit-content',
            whiteSpace: 'nowrap',
            borderRadius: '6px',
            padding: '0 10px',
            textAlign: 'center',
            fontWeight: '700',
            fontSize: '1.1em',
          }}
        >
          {entityData.name}
        </Typography>
      </Box>

      {errorMessage?.length === 0 ? (
        <Card sx={{ padding: '24px 24px 6px' }}>
          {/* user search filled */}
          <Box sx={{ margin: '0px 0px 20px' }}>
            <CustomTextField
              size="small"
              fullWidth
              id="national-org-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Cerca..."
              onChange={onSearchUser}
            />
          </Box>

          {/* federation club user table */}
          <TableContainer component={Paper}>
            <Table size="small" aria-label="User table">
              {/* responsive user table header */}
              <TableHead>
                <TableRow>
                  <TableCell>Nome Utente</TableCell>
                  <TableCell>Codice Fiscale</TableCell>
                  {is400Screen && (
                    <>
                      <TableCell align="center">Data di Nascita</TableCell>
                    </>
                  )}
                  {is600Screen && (
                    <>
                      <TableCell align="center">Verificato</TableCell>
                      <TableCell align="center">Tesserato</TableCell>
                      <TableCell align="center">Online</TableCell>
                      <TableCell align="center">Attivo</TableCell>
                      <TableCell align="center">Fase</TableCell>
                    </>
                  )}
                  <TableCell />
                </TableRow>
              </TableHead>

              {/* responsive user table body */}
              {userList.length !== 0 ? (
                <TableBody>
                  {(rowsPerPage > 0
                    ? userList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : userList
                  ).map((user) => (
                    <TableRow hover key={user.user_id}>
                      <TableCell>
                        {user.name_alias
                          ? `${user.name_alias} [${user.name} ${user.surname}]`
                          : `${user.name} ${user.surname}`}
                      </TableCell>
                      <TableCell>{user.fiscal_code}</TableCell>
                      {is400Screen && (
                        <>
                          <TableCell align="center">{dayjs(user.birth_date).format('DD/MM/YYYY')}</TableCell>
                        </>
                      )}
                      {is600Screen && (
                        <>
                          <TableCell align="center">
                            {user.verified ? (
                              <GppGoodIcon sx={{ color: '#47ba25', fontSize: '1.8em' }} />
                            ) : (
                              <GppBadIcon sx={{ color: 'red', fontSize: '1.8em' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {user.card_holder ? (
                              <TurnedInIcon sx={{ color: '#329dd6' }} />
                            ) : (
                              <TurnedInNotIcon sx={{ color: '#9fa1a2' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {user.user_online ? (
                              <PublicIcon sx={{ color: '#47ba25' }} />
                            ) : (
                              <PublicIcon sx={{ color: '#9fa1a2' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {user.user_status ? (
                              <Lock sx={{ color: 'red' }} />
                            ) : (
                              <LockOpenIcon sx={{ color: '#47ba25' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <CommonLabel
                              textColor="white"
                              bgColor={
                                user.pipeline_stage === 'contact'
                                  ? '#1a73e8'
                                  : user.pipeline_stage === 'member'
                                  ? 'green'
                                  : 'red'
                              }
                              text={
                                user.pipeline_stage === 'contact'
                                  ? 'Contatto'
                                  : user.pipeline_stage === 'member'
                                  ? 'Membro'
                                  : 'Utente'
                              }
                            />
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <CustomIconButton
                          size="small"
                          onClick={() => navigate('/admin/users/user-details', { state: { userID: user.user_id } })}
                        >
                          <KeyboardArrowRight />
                        </CustomIconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* empty user table row */}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 47 * emptyRows }}>
                      <TableCell colSpan={9} />
                    </TableRow>
                  )}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                      Nessun utente trovato
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              {/* federation user empty table footer */}
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{ borderBottom: 'none' }}
                    rowsPerPageOptions={[]}
                    colSpan={9}
                    count={userList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        'aria-label': 'Users per page',
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        ErrorUI(errorMessage)
      )}
    </Container>
  ) : (
    // loading spinner UI
    LoadingUI()
  );
}
